import InputField from "../../../components/common/form/InputField";
import SelectField from "../../../components/common/form/SelectField";
import {
    createOptionListForSelectTag,
    getBooleanDisplayIcon,
} from "../../../helpers/utils/Utils";
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { useGetAllStatesQuery } from "../../data/states/statesApi";
import Form from "../../../components/common/form/Form";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import { useCreateBranchMutation, useGetBranchByIdQuery } from "../branchApi";
import { useGetCustomerByIdQuery } from "../customerApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useUpdateBranchMutation } from "../branchApi";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import ButtonTray from "../../../components/common/form/ButtonTray";
import CommonChosenInput from "../../order/editor/Sections/CommonChosenInput";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface props {
    branchId?: string,
    customerId: string | undefined,
    setShow?: any,
    btnText: string,
    cancelBtnText: string
    isEditable?: boolean
    labelClass?: boolean
}

const BranchForm: FC<props> = ({
    branchId,
    customerId,
    setShow,
    btnText,
    cancelBtnText,
    isEditable = true,
    labelClass = false
}) => {

    const { data: enums } = useGetEnumsQuery("");
    const statesApiResult = useGetAllStatesQuery("");
    const [branchData, setBranchData] = useState<any>({});
    const [createBranchApi, createBranchApiResponse] = useCreateBranchMutation();
    const [updateBranchApi, updateBranchApiResponse] = useUpdateBranchMutation();
    const customerQuery = useGetCustomerByIdQuery(customerId ?? skipToken);
    const [branchSuffix, setBranchSuffix] = useState([]);
    const [showButtons, setShowButtons] = useState(false);
    const isCreateMode = !branchId;
    const isReadOnly = !isCreateMode ? !hasPermission(["BRAN-U"]) : !hasPermission(["BRAN-C"]);
    type OptionType = { label: string; value: string };
    const [deliveryWhitelist, setDeliveryWhitelist] = useState<OptionType[]>([]);
    const branchQuery = useGetBranchByIdQuery(
        branchId ? {
            customerId: customerId,
            branchId: branchId
        } : skipToken,
    );

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setBranchData({
            ...branchData,
            [name]: value,
        })
        if (btnText !== "Create") {
            setShowButtons(true)
        }
    }

    useEffect(() => {
        let obj = branchQuery?.data;
        if (!obj)
            setBranchData({
                preferredState: {
                    value: "TX",
                    label: "Texas"
                },
                preferredHoaVendor: {
                    value: "TGX",
                    label: "TitleLogix"
                },
                includedParcelCount: 3,
                includeCadPrintout: true,
                revenueEnabled: true
            })
    }, []);

    useEffect(() => {
        let obj = branchQuery?.data;
        if (obj) {
            setBranchData(transformBranchData(obj));
            setBranchSuffix(
                obj.branchSuffix.map((el: any) => ({
                    value: el,
                    label: el
                }))
            );
            if (Array.isArray(enums?.["ProductType"])) {
                const whitelist = enums["ProductType"]
                    .filter((p: any) => p.constant !== "UNKNOWN")
                    .filter((p: any) => !obj.deliveryBlacklist.includes(p.constant))
                    .map((p: any) => ({
                        label: p.value,
                        value: p.constant
                    }));
                setDeliveryWhitelist(whitelist);
            }
        }
    }, [branchQuery?.data, statesApiResult?.data]);


    const handleToggle = (property: string) => (e: any) => {

        if (property === "packagedDelivery") {
            setBranchData((prevData: any) => ({
                ...prevData,
                useCombinedPdf: false,
                [property]: !prevData[property]
            }));
        }
        else {
            setBranchData((prevData: any) => ({
                ...prevData,
                [property]: !prevData[property]

            }));
        }
        setShowButtons(true);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const selectedWhitelistConstants = deliveryWhitelist.map((el: any) => el.value); // these are `constant`s
        const allConstants = enums?.["ProductType"]
            ?.filter((p: any) => p.constant !== "UNKNOWN")
            ?.map((p: any) => p.constant) || [];

        const deliveryBlacklist = allConstants.filter((c: any) => !selectedWhitelistConstants.includes(c));

        let obj = {
            availableProduct: branchData.availableProduct?.map((el: any) => el.value) || [],
            defaultProducts: branchData.defaultProducts?.map((el: any) => el.value) || [],
            branchSuffix: branchSuffix.map((el: any) => el.value),
            customerId: customerId,
            defaultBranch: branchData.defaultBranch ?? false,
            deliveryBlacklist,
            emailNotificationMode: branchData.emailNotificationMode?.value,
            feeDisplayType: branchData.feeDisplayType.value,
            id: branchId,
            includeCadPrintout: branchData.includeCadPrintout,
            includedParcelCount: branchData.includedParcelCount,
            includeOriginalPropertyNote: branchData.includeOriginalPropertyNote,
            name: branchData.name,
            packagedDelivery: branchData.packagedDelivery,
            partnerBranchId: branchData.partnerBranchId,
            preferredHoaVendor: branchData.preferredHoaVendor.value,
            preferredState: branchData.preferredState?.value,
            preliminaryHoa: branchData.preliminaryHoa,
            revenueEnabled: branchData.revenueEnabled,
            salesTaxRate: branchData.salesTaxRate,
            tenant: customerQuery?.data.tenant,
            useCombinedPdf: branchData.useCombinedPdf
        };

        try {
            if (btnText === "Create") {
                await handleRTKQuery(
                    async () => await createBranchApi({
                        customerId: customerQuery?.data.customerId,
                        requestBody: obj
                    }).unwrap(),
                    () => {
                        setBranchData({});
                        setShow(false);
                    }
                );
            } else {
                await updateBranchApi({
                    customerId: customerQuery?.data.customerId,
                    branchId: branchId,
                    requestBody: obj,
                }).unwrap()
                setShowButtons(false);
            }
        } catch (error) {
            console.error("Failed to submit form:", error);
        }
    };

    const getWhiteListArray = (deliveryBlacklist: string[]): any[] => {
        if (enums?.["ProductType"] && deliveryBlacklist && deliveryBlacklist.length > 0) {
            return enums["ProductType"].filter((productType: any) =>
                !deliveryBlacklist.includes(productType.constant)
            );
        }
        return [];
    };

    const handleCloseBtnClick = () => {
        if (btnText === "Create") {
            setShow(false);
        } else {
            let obj = branchQuery?.data;
            if (obj) {
                setBranchData(transformBranchData(obj));
            }


            setShowButtons(false);
        }
    };

    const transformBranchData = (obj: any) => {
        if (!obj) return null;

        return {
            ...obj,
            preferredState: obj.preferredState
                ? {
                    label: statesApiResult?.data?.find(
                        (el: any) => el.stateAbbreviation === obj.preferredState
                    )?.stateName,
                    value: obj.preferredState,
                }
                : null,
            preferredHoaVendor: obj.preferredHoaVendor
                ? {
                    value: obj.preferredHoaVendor,
                    label: getEnumDisplayNameByConstant(
                        enums?.["VendorEnum"],
                        obj.preferredHoaVendor
                    ),
                }
                : null,
            emailNotificationMode: obj.emailNotificationMode
                ? {
                    value: obj.emailNotificationMode,
                    label: getEnumDisplayNameByConstant(
                        enums?.["EmailNotificationMode"],
                        obj.emailNotificationMode
                    ),
                }
                : null,
            feeDisplayType: obj.feeDisplayType
                ? {
                    value: obj.feeDisplayType,
                    label: getEnumDisplayNameByConstant(
                        enums?.["FeeDisplayType"],
                        obj.feeDisplayType
                    ),
                }
                : null,
            availableProduct: obj?.availableProduct
                ? obj?.availableProduct.map((el: any) => ({
                    value: el,
                    label: getEnumDisplayNameByConstant(
                        enums?.["ProductType"],
                        el
                    ),
                }))
                : [],
            defaultProducts: obj?.defaultProducts
                ? obj?.defaultProducts.map((el: any) => ({
                    value: el,
                    label: getEnumDisplayNameByConstant(
                        enums?.["ProductType"],
                        el
                    ),
                }))
                : [],
            deliveryWhitelist: obj?.deliveryBlacklist
                ? getWhiteListArray(obj.deliveryBlacklist).map((el) => ({
                    value: el.constant,
                    label: el.value,
                }))
                : [],
        };
    };

    return (
        <>
            <Form onSubmit={handleSubmit} fields={
                <>
                    <div className="absolute right-3 top-[-12px]">
                        {showButtons && (
                            <ButtonTray
                                buttons={[
                                    {
                                        buttonProps: {
                                            btnText: "Cancel",
                                            onClick: handleCloseBtnClick,
                                            type: "close",
                                        },
                                        buttonType: "button"
                                    },
                                    {
                                        buttonProps: {
                                            btnText: "Update",
                                            type: "submit",
                                            isLoading: (createBranchApiResponse.isLoading || updateBranchApiResponse.isLoading),
                                            isVisible: isEditable
                                        },
                                        buttonType: "button",
                                    }
                                ]}
                                align="end"
                                gap={2}
                            />)}
                    </div>

                    <InputField
                        className="mb-2"
                        name={"name"}
                        label="Branch Name"
                        onChange={handleOnChange}
                        value={branchData?.name || ""}
                        type="text"
                        placeholder="Enter Branch Name..."
                        required={true}
                        disabled={isReadOnly}
                    />

                    {btnText === "Update" && (
                        <div className="col-span-12 md:grid md:grid-cols-12 gap-3 mb-2">
                            <div className="col-span-3 sm:mt-2 lg-mt-0 mt-2 flex items-center">
                                <div className="text-surface-0">Is Default</div>
                            </div>
                            <div className="col-span-9 mt-2">

                                {getBooleanDisplayIcon(branchData?.defaultBranch, "Enabled", "Disabled", '')}

                            </div>
                        </div>

                    )}


                    <SelectField
                        placeholder='Select Available Products...'
                        value={branchData?.availableProduct || null}
                        options={createOptionListForSelectTag(enums?.["ProductType"] && enums?.["ProductType"], "value", "constant")}
                        onChange={(selectedOption) => {
                            handleOnChange({ name: "availableProduct", value: selectedOption })
                        }}
                        label={"Available Products"}
                        isMulti={true}
                        labelClassName={!labelClass ? 'font-normal text-surface-0' : 'text-surface-0'}
                        className={'font-normal relative col-span-9 mb-2'}
                        isDisabled={isReadOnly}
                    />
                    <SelectField
                        placeholder='Select Default Products...'
                        value={branchData?.defaultProducts || null}
                        options={
                            createOptionListForSelectTag(
                                enums?.["ProductType"]?.filter((item: any) => {
                                    const availableConstants = branchData?.availableProduct?.map((p: any) => p.value) || [];
                                    return item.constant !== "NTP" && availableConstants.includes(item.constant);
                                }),
                                "value",
                                "constant"
                            )
                        }
                        onChange={(selectedOption) => {
                            handleOnChange({ name: "defaultProducts", value: selectedOption })
                        }}
                        label={"Default Products"}
                        isMulti={true}
                        labelClassName={!labelClass ? 'font-normal text-surface-0' : 'text-surface-0'}
                        className={'font-normal relative col-span-9 mb-2'}
                        isDisabled={isReadOnly}
                    />

                    <SelectField
                        placeholder="Select Auto Delivery..."
                        value={deliveryWhitelist}
                        options={createOptionListForSelectTag(enums?.["ProductType"] &&
                            enums?.["ProductType"].filter((cur: any) => cur.constant !== "UNKNOWN"),
                            "value",
                            "constant"

                        )}
                        onChange={(selectedOptions) => {
                            setDeliveryWhitelist((selectedOptions || []) as OptionType[]);
                            if (btnText !== "Create") {
                                setShowButtons(true)
                            }
                        }}
                        label={"Auto Delivery"}
                        isMulti={true}
                        className={'font-normal relative col-span-9 mb-2'}
                        isDisabled={isReadOnly}
                    />

                    <SelectField
                        placeholder='Select Email Notification Mode...'
                        value={branchData?.emailNotificationMode || null}
                        options={createOptionListForSelectTag(enums?.["EmailNotificationMode"] && enums?.["EmailNotificationMode"], "value", "constant")}
                        onChange={(selectedOption) => {
                            handleOnChange({ name: "emailNotificationMode", value: selectedOption })
                        }}
                        label={"Email Notification Mode"}
                        required={true}
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        className={'font-normal relative col-span-9'}
                        isDisabled={isReadOnly}
                    />

                    <InputField
                        name={"soaNotificationEmail"}
                        label="SOA Notification Email"
                        onChange={handleOnChange}
                        value={branchData?.soaNotificationEmail || ""}
                        type="text"
                        placeholder="Enter SOA Notification Email..."
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        disabled={isReadOnly}
                        className={'font-normal relative col-span-9 mb-2'}
                    />

                    <SelectField
                        placeholder='Select Preferred State...'
                        value={branchData?.preferredState}
                        options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "stateAbbreviation")}
                        onChange={(selectedOption) => {
                            handleOnChange({ name: "preferredState", value: selectedOption })
                        }}
                        label={"Preferred State"}
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        className={'font-normal relative col-span-9 mb-2'}
                        isDisabled={isReadOnly}
                    />

                    <SelectField
                        placeholder='Select Preferred HOA Vendor...'
                        value={branchData?.preferredHoaVendor || null}
                        options={createOptionListForSelectTag(enums?.["VendorEnum"]
                            && enums?.["VendorEnum"].filter((el: any) => {
                                return (el.constant === 'AVANTA' || el.constant === 'TGX')
                            }), "value", "constant")}
                        onChange={(selectedOption) => {
                            handleOnChange({ name: "preferredHoaVendor", value: selectedOption })
                        }}
                        label={"Preferred HOA Vendor"}
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        className={'font-normal relative col-span-9 '}
                        required
                        isDisabled={isReadOnly}
                    />

                    <InputField
                        name={"salesTaxRate"}
                        label="Sales Tax Rate"
                        onChange={handleOnChange}
                        value={branchData?.salesTaxRate || ""}
                        type="number"
                        placeholder="Enter Sales Tax Rate..."
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        disabled={isReadOnly}
                    />
                    <div className="col-span-12 md:grid md:grid-cols-12 gap-3 !font-normal">
                        <div className="col-span-3 sm:mt-2 lg-mt-0 mt-2 flex items-center">
                            <label htmlFor=""
                                className={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}>Branch
                                Suffix</label>
                        </div>
                        <div className="col-span-9 mt-2">
                            <CommonChosenInput placeholder={"Enter Branch Suffix..."}
                                state={branchSuffix}
                                setState={setBranchSuffix} isRequired={false}
                                isDisable={isReadOnly}
                            />
                        </div>
                    </div>


                    <InputField
                        name={"partnerBranchId"}
                        label="Partner Branch ID"
                        onChange={handleOnChange}
                        value={branchData?.partnerBranchId || ""}
                        type="text"
                        placeholder="Enter Partner Branch ID..."
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        disabled={isReadOnly}
                    />

                    <InputField
                        name={"includedParcelCount"}
                        label="Included Parcel Count"
                        onChange={handleOnChange}
                        value={branchData?.includedParcelCount || ""}
                        type="number"
                        placeholder="Included Parcel Count..."
                        required
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        disabled={isReadOnly}
                    />

                    <SelectField
                        placeholder='Select Fee Display Type...'
                        value={branchData?.feeDisplayType || null}
                        options={createOptionListForSelectTag(enums?.["FeeDisplayType"], "value", "constant")}
                        onChange={(selectedOption) => {
                            handleOnChange({ name: "feeDisplayType", value: selectedOption })
                        }}
                        label={"Fee Display Type"}
                        required={true}
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        className={'font-normal relative col-span-9 mb-2 mt-2'}
                        isDisabled={isReadOnly}
                    />


                    <div className={"mb-3 mt-2"}>
                        <InputField
                            name={"preliminaryHoa"}
                            label="Preliminary HOA"
                            onClick={handleToggle("preliminaryHoa")}
                            checked={branchData?.preliminaryHoa || ""}
                            type='checkbox'
                            labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                            disabled={isReadOnly}
                        />
                    </div>
                    <div className={"mb-3"}>
                        <InputField
                            name={"includeCadPrintout"}
                            label="Include Cad Printout"
                            onClick={handleToggle("includeCadPrintout")}
                            checked={branchData?.includeCadPrintout || ""}
                            type='checkbox'
                            labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                            disabled={isReadOnly}
                        />
                    </div>
                    <div className={"mb-3"}><InputField
                        name={"packagedDelivery"}
                        label="Packaged Delivery"
                        onClick={handleToggle("packagedDelivery")}
                        checked={branchData?.packagedDelivery || ""}
                        type='checkbox'
                        labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                        disabled={isReadOnly}
                    /></div>
                    <div className={"mb-3"}>
                        <InputField
                            name={"includeOriginalPropertyNote"}
                            label="Include Original Property Note"
                            onClick={handleToggle("includeOriginalPropertyNote")}
                            checked={branchData?.includeOriginalPropertyNote || ""}
                            type='checkbox'
                            labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                            disabled={isReadOnly}
                        />
                    </div>
                    <div className={"mb-3"}>
                        <InputField
                            name={"useCombinedPdf"}
                            label="Combined PDF"
                            onClick={handleToggle("useCombinedPdf")}
                            checked={branchData?.useCombinedPdf || ""}
                            type='checkbox'
                            labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                            disabled={!branchData?.packagedDelivery}
                        />
                    </div>
                    <div className={"mb-3"}>
                        <InputField
                            name={"revenueEnabled"}
                            label="Is Revenue Enabled"
                            onClick={handleToggle("revenueEnabled")}
                            checked={branchData?.revenueEnabled || ""}
                            type='checkbox'
                            labelClassName={!labelClass ? 'font-normal text-text-surface-0' : 'text-surface-0'}
                            disabled={isReadOnly}
                        />
                    </div>
                    {btnText !== "Update" && (
                        <div className={'mt-4 border-t border-t-surface-5 flex justify-end pb-2'}>
                            <ButtonTray
                                buttons={[
                                    {
                                        buttonProps: {
                                            btnText: "Cancel",
                                            onClick: handleCloseBtnClick,
                                            type: "close",
                                        },
                                        buttonType: "button"
                                    },
                                    {
                                        buttonProps: {
                                            btnText: "Submit",
                                            type: "submit",
                                            isLoading: (createBranchApiResponse.isLoading || updateBranchApiResponse.isLoading),
                                            isVisible: isEditable
                                        },
                                        buttonType: "button",

                                    }
                                ]}
                                align="end"
                                gap={2}
                            />
                        </div>
                    )}


                </>
            } />

        </>
    )
}
export default BranchForm;